<template>
  <div class="wg-modal-back" @click="$emit('close')">
    <div class="wg-modal wide" role="dialog" aria-modal="true" @click.stop>
      <button class="wg-modal-x" aria-label="Close" @click="$emit('close')">
        <i class="material-icons">close</i>
      </button>

      <div class="wg-cmhead">
        <div class="wg-cmico"><i class="material-icons-outlined">trending_up</i></div>
        <h3>{{ card.label }}</h3>
      </div>

      <div class="wg-chart-big">
        <div class="cb-top">
          <span class="cb-n">{{ card.value }}</span>
          <span class="cb-d" :class="{ flat: card.flat }">
            <template v-if="!card.flat">▲ </template>{{ card.deltaText }}
          </span>
        </div>
        <wins-sparkline :data="card.series" dir="up" :width="620" :height="200" />
        <div class="cb-axis">
          <span>{{ axisDate(periodStart) }}</span>
          <span>{{ axisDate(periodEnd) }}</span>
        </div>
        <div v-if="card.lowerIsBetter" class="cb-note">
          Google positions: a lower number is better (position 1 is the top of page 1).
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Zoomed-in trendline for a metric card (design_updated9): the small
 * sparkline on a card opens here at full width, with the charted window's
 * real dates on the axis.
 */
import moment from 'moment'
import WinsSparkline from '@/components/SitesSingleView/WhiteGlove/Wins/WinsSparkline.vue'

export default {
  name: 'WinsChartModal',
  components: { WinsSparkline },
  props: {
    card: {
      type: Object,
      required: true,
    },
    /** ISO dates of the charted window (snapshot periodStart/periodEnd). */
    periodStart: {
      type: String,
      default: '',
    },
    periodEnd: {
      type: String,
      default: '',
    },
  },
  computed: {
    sameYear () {
      return this.periodStart && this.periodEnd &&
        this.periodStart.slice(0, 4) === this.periodEnd.slice(0, 4)
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    axisDate (iso) {
      if (!iso) return ''
      return moment(iso).format(this.sameYear ? 'MMM D' : 'MMM D, YYYY')
    },
    onKeydown (event) {
      if (event.key === 'Escape') this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Settings/settings-card.scss';

.wg-modal-back {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
}

.wg-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  box-shadow: $shadow-menu;
  width: 100%;
  padding: 28px 30px 24px;

  &.wide {
    max-width: 700px;
  }
}

.wg-modal-x {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: $pop-ink-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: $pop-box-bg;
    color: $pop-teal;
  }

  .material-icons {
    font-size: 1.2rem;
  }
}

.wg-cmhead {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  padding-right: 26px;

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0;
  }
}

.wg-cmico {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: $pop-accent-bg;
  color: $pop-accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  .material-icons-outlined {
    font-size: 1.5rem;
  }
}

/* zoomed trendline (design mock .wg-chart-big) */
.wg-chart-big {
  margin-top: 6px;

  .cb-top {
    display: flex;
    align-items: baseline;
    gap: 14px;
    margin-bottom: 14px;
  }

  .cb-n {
    font-size: 2rem;
    font-weight: 800;
    color: $pop-teal;
  }

  .cb-d {
    font-size: 0.85rem;
    font-weight: 700;
    color: $pop-success;

    &.flat {
      color: $pop-ink-faint;
    }
  }

  ::v-deep .wg-spark {
    width: 100%;
    height: auto;
  }

  .cb-axis {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: $pop-ink-faint;
    margin-top: 6px;
  }

  .cb-note {
    font-size: 0.78rem;
    color: $pop-ink-muted;
    margin-top: 12px;
  }
}
</style>
